/**
 * ParticleCanvas — canvas-based Apple-Watch-style particle ring.
 * Self-contained, adaptive FPS tuning, reduced-motion fallback, aria-hidden.
 */
import { useRef, useEffect, useState } from 'react'

type ParticleCanvasProps = {
  active: boolean
  size?: number
  ringRadius?: number
  colors?: string[]
}

type Particle = {
  angle: number
  orbit: number
  size: number
  color: string
  speed: number
  phase: number
}

function usePrefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !('matchMedia' in window)) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function parseHexRGB(hex: string): [number, number, number] {
  const h = hex.replace('#', '')
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ]
}

function hexWithAlpha(hex: string, alpha: number): string {
  const [r, g, b] = parseHexRGB(hex)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/** Lerp between two hex colors by t in [0,1], return hex string */
function lerpColor(from: string, to: string, t: number): string {
  const [r1, g1, b1] = parseHexRGB(from)
  const [r2, g2, b2] = parseHexRGB(to)
  const r = Math.round(r1 + (r2 - r1) * t)
  const g = Math.round(g1 + (g2 - g1) * t)
  const b = Math.round(b1 + (b2 - b1) * t)
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

function drawSoftDot(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  pxSize: number,
  color: string,
  alpha = 1
) {
  ctx.save()
  const grad = ctx.createRadialGradient(x, y, 0, x, y, Math.max(pxSize * 2.5, pxSize * 3))
  grad.addColorStop(0, hexWithAlpha(color, alpha))
  grad.addColorStop(0.3, hexWithAlpha(color, Math.max(0.28, alpha * 0.45)))
  grad.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = grad
  ctx.beginPath()
  ctx.arc(x, y, pxSize * 2.3, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

export default function ParticleCanvas({
  active,
  size = 56,
  ringRadius = 36,
  colors = ['#ffffff', '#38bdf8', '#a78bfa'],
}: ParticleCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const rafRef = useRef<number | null>(null)
  const lastTimeRef = useRef<number | null>(null)
  const fpsHistory = useRef<number[]>([])
  const particlesRef = useRef<Particle[]>([])
  const activeRef = useRef(active)
  const [particleCount, setParticleCount] = useState<number>(12)
  const reduced = usePrefersReducedMotion()
  /* Color transition state — lerp from previous palette to new over 800ms */
  const prevColorsRef = useRef<string[]>(colors)
  const targetColorsRef = useRef<string[]>(colors)
  const colorTransitionRef = useRef(1) // 0..1, starts "complete"
  const COLOR_LERP_DURATION = 0.8 // seconds
  // Keep activeRef in sync so the rAF loop reads live value without restarting
  useEffect(() => { activeRef.current = active }, [active])

  // Detect color prop changes and start a smooth color transition
  useEffect(() => {
    const prev = targetColorsRef.current
    const changed = colors.length !== prev.length || colors.some((c, i) => c !== prev[i])
    if (changed) {
      // Snapshot current visual colors (lerped state) as the new "from"
      const t = colorTransitionRef.current
      prevColorsRef.current = prev.map((p, i) => {
        const fromC = prevColorsRef.current[i % prevColorsRef.current.length]
        return t >= 1 ? p : lerpColor(fromC, p, t)
      })
      targetColorsRef.current = colors
      colorTransitionRef.current = 0
    }
  }, [colors])

  // Initial particle count from hardware heuristics
  useEffect(() => {
    const concurrency = (navigator as unknown as { hardwareConcurrency?: number }).hardwareConcurrency ?? 4
    const dpr = Math.max(1, window.devicePixelRatio || 1)
    const perfFactor = Math.max(0.5, Math.min(1.6, concurrency / 4 / Math.sqrt(dpr)))
    const initial = Math.round(12 * perfFactor)
    setParticleCount(Math.max(6, Math.min(20, initial)))
  }, [])

  // Rebuild particles when dimensions or count change
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const dpr = Math.max(1, window.devicePixelRatio || 1)
    canvas.style.width = `${size}px`
    canvas.style.height = `${size}px`
    canvas.width = Math.round(size * dpr)
    canvas.height = Math.round(size * dpr)
    const ctx = canvas.getContext('2d')
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

    particlesRef.current = Array.from({ length: particleCount }).map((_, i) => ({
      angle: (i / Math.max(1, particleCount)) * Math.PI * 2 + (Math.random() * 0.2 - 0.1),
      orbit: ringRadius + (Math.random() * 8 - 4),
      size: Math.max(3, Math.round(Math.random() * 3 + 3)),
      color: colors[i % colors.length],
      speed: 0.2 + Math.random() * 0.8,
      phase: Math.random() * Math.PI * 2,
    }))
    fpsHistory.current = []
  }, [size, ringRadius, particleCount, colors])

  // Animation / static-halo loop
  useEffect(() => {
    const canvas = canvasRef.current as HTMLCanvasElement
    if (!canvas) return
    const ctx = canvas.getContext('2d')!
    let mounted = true

    if (reduced) {
      // Static halo for reduced-motion
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      const cx = size / 2
      const cy = size / 2
      for (let i = 0; i < Math.min(6, particleCount); i++) {
        const angle = (i / Math.max(1, particleCount)) * Math.PI * 2
        const rad = ringRadius + (i % 3) * 2
        drawSoftDot(ctx, cx + Math.cos(angle) * rad, cy + Math.sin(angle) * rad, 5, colors[i % colors.length], 0.45)
      }
      return
    }

    const TARGET_FPS = 50
    const MIN_FPS = 35

    function step(t: number) {
      if (!mounted) return
      if (lastTimeRef.current == null) lastTimeRef.current = t
      const dt = (t - lastTimeRef.current) / 1000
      lastTimeRef.current = t

      const fps = dt > 0 ? 1 / dt : TARGET_FPS
      fpsHistory.current.push(fps)
      if (fpsHistory.current.length > 30) fpsHistory.current.shift()
      const meanFPS = fpsHistory.current.reduce((a, b) => a + b, 0) / fpsHistory.current.length

      // Adaptive particle count
      if (meanFPS < MIN_FPS && particleCount > 6) {
        setParticleCount(n => Math.max(6, Math.round(n * 0.8)))
      } else if (meanFPS > TARGET_FPS && particleCount < 20) {
        setParticleCount(n => Math.min(20, n + 1))
      }

      const cx = size / 2
      const cy = size / 2
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Subtle background halo
      ctx.save()
      const halo = ctx.createRadialGradient(cx, cy, 2, cx, cy, ringRadius + 40)
      halo.addColorStop(0, 'rgba(167,139,250,0.04)')
      halo.addColorStop(1, 'rgba(10,10,12,0)')
      ctx.fillStyle = halo
      ctx.beginPath()
      ctx.arc(cx, cy, ringRadius + 50, 0, Math.PI * 2)
      ctx.fill()
      ctx.restore()

      const particles = particlesRef.current

      // Sync list length to current count
      if (particles.length > particleCount) {
        particlesRef.current = particles.slice(0, particleCount)
      } else if (particles.length < particleCount) {
        const toAdd = particleCount - particles.length
        for (let i = 0; i < toAdd; i++) {
          particlesRef.current.push({
            angle: Math.random() * Math.PI * 2,
            orbit: ringRadius + (Math.random() * 8 - 4),
            size: Math.max(3, Math.round(Math.random() * 3 + 3)),
            color: colors[(particles.length + i) % colors.length],
            speed: 0.3 + Math.random() * 0.8,
            phase: Math.random() * Math.PI * 2,
          })
        }
      }

      /* Advance color transition lerp */
      if (colorTransitionRef.current < 1) {
        colorTransitionRef.current = Math.min(1, colorTransitionRef.current + dt / COLOR_LERP_DURATION)
      }
      const cT = colorTransitionRef.current
      const prevC = prevColorsRef.current
      const tgtC = targetColorsRef.current

      const speedMult = activeRef.current ? 2.2 : 1.0
      for (let i = 0; i < particlesRef.current.length; i++) {
        const p = particlesRef.current[i]
        p.phase += p.speed * dt * speedMult
        const wobble = Math.sin(p.phase * 1.2 + i) * 2
        const theta = p.angle + p.phase * 0.12
        const x = cx + Math.cos(theta) * (p.orbit + wobble)
        const y = cy + Math.sin(theta) * (p.orbit + wobble)
        /* Smoothly interpolate particle color during transition */
        const fromColor = prevC[i % prevC.length] ?? '#ffffff'
        const toColor = tgtC[i % tgtC.length] ?? '#ffffff'
        const renderColor = cT >= 1 ? toColor : lerpColor(fromColor, toColor, cT)
        drawSoftDot(ctx, x, y, p.size, renderColor, 0.85 - Math.abs(Math.sin(p.phase * 0.5)) * 0.45)
      }

      rafRef.current = requestAnimationFrame(step)
    }

    rafRef.current = requestAnimationFrame(step)
    return () => {
      mounted = false
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
      lastTimeRef.current = null
    }
  }, [size, ringRadius, particleCount, colors, reduced])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ pointerEvents: 'none', display: 'block' }}
      width={size}
      height={size}
    />
  )
}
